import { useCallback, useState } from 'react';
import { Text, Flex, useToast, Stack, VStack, Skeleton, Button } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useGetConfigSMTP, usePutConfigSMTPMutation } from '@/services/queries/admin-config';
import { InputField } from '@/components/InputField';
import { PutAdminConfigSMTPRequest } from '@/services/request/admin-config';
import { ModalFormData, modalFormSchema } from './schemas';
import { useTranslation } from 'react-i18next';

export const SMTPConfigTab = () => {
  const { t } = useTranslation();
  const [selectedInput, setSelectedInput] = useState('');
  const toast = useToast();

  const putItemMutation = usePutConfigSMTPMutation();
  const { refetch, isLoading } = useGetConfigSMTP(false);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PutAdminConfigSMTPRequest = {
        from: data.from,
        host: data.host,
        port: data.port,
        username: data.username,
        reply_to: data.reply_to,
        password: data.password,
      };
      putItemMutation.mutate(payload, {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t('pages.communication_central.components.smtp_config_tab.smtp_config_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.communication_central.components.smtp_config_tab.smtp_config_error'),
          });
        },
      });
    },
    [putItemMutation, toast, t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ModalFormData>({
    defaultValues: async () => {
      const { data } = await refetch();
      return {
        host: data?.host ?? '',
        username: data?.username ?? '',
        password: '',
        port: data?.port ?? 587,
        from: data?.from ?? '',
        reply_to: data?.reply_to ?? '',
      };
    },
    resolver: zodResolver(modalFormSchema),
  });

  const registerWithoutBlur = useCallback(
    (fieldName: keyof ModalFormData) => {
      const { name, onChange, ref, disabled, max, maxLength, min, minLength, pattern, required } = register(fieldName);

      return { name, onChange, ref, disabled, max, maxLength, min, minLength, pattern, required };
    },
    [register],
  );

  return (
    <Flex direction='column'>
      <Flex justifyContent='space-between' alignItems='center' gap={2} mb={6}>
        <Text fontWeight='bold'>{t('pages.communication_central.components.smtp_config_tab.smtp_config_title')}</Text>
      </Flex>

      <Stack flex={1}>
        <VStack as='form' gap='kilo' align='start' onSubmit={handleSubmit(onSubmit)} maxWidth={{ base: '100%', md: '30%' }}>
          {isLoading ? (
            <>
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
              <Skeleton width='100%' h='40px' />
            </>
          ) : (
            <>
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.smtp_config_tab.smtp_config_host_label')}
                type={selectedInput === 'host' ? 'text' : 'password'}
                onFocus={() => setSelectedInput('host')}
                onBlur={() => setSelectedInput('')}
                rootProps={{ isInvalid: !!errors?.host, errorMessage: errors?.host?.message }}
                {...registerWithoutBlur('host')}
              />
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.smtp_config_tab.smtp_config_username_label')}
                rootProps={{ isInvalid: !!errors?.username, errorMessage: errors?.username?.message }}
                {...register('username')}
              />
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.smtp_config_tab.smtp_config_password_label')}
                type={selectedInput === 'password' ? 'text' : 'password'}
                onFocus={() => setSelectedInput('password')}
                onBlur={() => setSelectedInput('')}
                rootProps={{ isInvalid: !!errors?.password, errorMessage: errors?.password?.message }}
                {...registerWithoutBlur('password')}
              />
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.smtp_config_tab.smtp_config_port_label')}
                type={selectedInput === 'port' ? 'text' : 'password'}
                onFocus={() => setSelectedInput('port')}
                onBlur={() => setSelectedInput('')}
                rootProps={{ isInvalid: !!errors?.port, errorMessage: errors?.port?.message }}
                {...registerWithoutBlur('port')}
              />
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.smtp_config_tab.smtp_config_from_label')}
                rootProps={{ isInvalid: !!errors?.from, errorMessage: errors?.from?.message }}
                {...register('from')}
              />
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.smtp_config_tab.smtp_config_reply_to_label')}
                rootProps={{ isInvalid: !!errors?.reply_to, errorMessage: errors?.reply_to?.message }}
                {...register('reply_to')}
              />
            </>
          )}

          {isLoading ? (
            <Skeleton width='138px' h='40px' />
          ) : (
            <Button type='submit'>{t('pages.communication_central.components.smtp_config_tab.smtp_config_submit_button')}</Button>
          )}
        </VStack>
      </Stack>

      <CenteredSpinner isLoading={isLoading || putItemMutation.isLoading} />
    </Flex>
  );
};
